import React from 'react'
import { Box, Heading, Text } from '@theme-ui/components'
import { Container, Markdown } from '~/Common'
import { useTranslation } from 'react-i18next'
import { theme } from 'app/theme'

export const CustomisableTable = ({ bg = 'white', data, container, ...props }) => {
  const { t } = useTranslation()
  const title = data?.title ?? null
  const description = data?.description ?? null
  const bottom = data?.bottom ?? null
  const rows = data?.rows ?? null
  const textAlign = data?.text_align ?? 'center'

  // Ensure number_of_columns is a number; default to 1 if not provided.
  const numberOfColumns = parseInt(data?.number_of_columns, 10) || 1

  return (
    <Box bg={bg} {...props}>
      <Container variant="layouts.section" {...container}>
        {title && (
          <Heading as="h2" variant="content_block_heading">
            {t(title)}
          </Heading>
        )}

        {description && (
          <Box className="markdown" mb={[3, 3, 4]}>
            <Markdown>{description}</Markdown>
          </Box>
        )}

        {rows && (
          <Box mb={[3, 3, 4]} sx={{ mt: 4, overflowX: 'auto', fontSize: '13px' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: [
                  `140px repeat(${numberOfColumns - 1}, minmax(10ch, max-content))`, // mobile
                  `140px repeat(${numberOfColumns - 1}, minmax(10ch, max-content))`, // tablet/desktop
                ],
                width: ['max-content', '100%'],
                minWidth: '100%',
                gap: 0,
              }}
            >
              {rows.map((row, rowIndex) =>
                Array.from({ length: numberOfColumns }).map((_, colIndex) => {
                  const colKey = `column_${colIndex + 1}`
                  const isFirstColumn = colIndex === 0
                  const isFirstRow = rowIndex === 0

                  return (
                    <Box
                      key={`${rowIndex}-${colIndex}`}
                      sx={{
                        position: isFirstColumn ? 'sticky' : 'static',
                        left: isFirstColumn ? '-2px' : 'auto',
                        py: '12px',
                        px: '8px',
                        textAlign: isFirstColumn ? 'center' : textAlign,
                        background:
                          isFirstRow && isFirstColumn
                            ? theme.colors.text
                            : isFirstColumn
                            ? theme.colors.medium
                            : 'white',
                        color: isFirstRow && isFirstColumn ? 'white' : theme.colors.text,
                        borderTop: rowIndex === 0 ? '1px solid black' : 'none',
                        borderBottom: '1px solid black',
                        minWidth: 0,
                      }}
                    >
                      <Text
                        as="p"
                        sx={{
                          m: 0,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          hyphens: 'auto',
                          maxWidth: ['42ch', '60ch'],
                          display: 'inline-block',
                        }}
                      >
                        {row[colKey] || ''}
                      </Text>
                    </Box>
                  )
                })
              )}
            </Box>
          </Box>
        )}

        {bottom && (
          <Box mt={2} sx={{ fontSize: '14px' }} className="markdown">
            <Markdown externalLinks={true}>{bottom}</Markdown>
          </Box>
        )}
      </Container>
    </Box>
  )
}
